<template>
	<div
		class="drop-zone"
		:class="{
			'drop-zone--active': isDragOver,
			'drop-zone--disabled': disabled
		}"
		@dragover.prevent.stop="handleDragOver"
		@dragenter.prevent.stop="handleDragEnter"
		@dragleave.prevent.stop="handleDragLeave"
		@drop.prevent.stop="handleDrop"
	>
		<div class="drop-zone__content">
			<slot />
		</div>

		<!-- Drop Indicator -->
		<div
			v-if="isDragOver"
			class="drop-zone__indicator"
		>
			<div class="drop-zone__indicator-content">
				<v-icon
					color="primary"
					size="20"
				>
					mdi-plus-circle
				</v-icon>
				<span class="drop-zone__indicator-text">
					Drop {{ draggedField ? draggedField.displayName : 'field' }} here
				</span>
			</div>
		</div>
	</div>
</template>

<script>
import { VIcon } from 'vuetify/lib/components'
import { showSnackbar } from '../../../../../../../shared/state/snackbar.js'
export default {
	name: 'drop-zone',
	components: {
		VIcon
	},
	props: {
		fieldUsageMap: {
			type: Object,
			default: () => ({})
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			isDragOver: false,
			draggedField: null
		}
	},
	methods: {
		handleDragOver (event) {
			if (this.disabled) return

			event.preventDefault()
			event.stopPropagation()

			// Check if we have valid field data
			if (!this.hasValidFieldData(event)) return

			event.dataTransfer.dropEffect = 'copy'
			this.isDragOver = true
		},

		handleDragEnter (event) {
			if (this.disabled) return

			event.preventDefault()
			event.stopPropagation()

			// Check if we have valid field data
			if (!this.hasValidFieldData(event)) return

			this.isDragOver = true

			// Try to extract field data for display
			try {
				const fieldData = this.extractFieldData(event)
				if (fieldData) {
					this.draggedField = fieldData.field
				}
			} catch (error) {
				// Ignore errors, we'll show generic message
			}
		},

		handleDragLeave (event) {
			if (this.disabled) return

			event.preventDefault()
			event.stopPropagation()

			// Only hide indicator if we're leaving the drop zone completely
			if (!this.$el.contains(event.relatedTarget)) {
				this.isDragOver = false
				this.draggedField = null
			}
		},

		handleDrop (event) {
			if (this.disabled) return

			event.preventDefault()
			event.stopPropagation()

			this.isDragOver = false

			try {
				const fieldData = this.extractFieldData(event)
				if (!fieldData) return

				// Get the target element (the actual input field)
				const targetElement = this.findTargetElement(event)
				if (!targetElement) return

				// Calculate cursor position for text insertion
				const insertionPosition = this.calculateInsertionPosition(targetElement, event)

				// Generate field token
				const token = this.generateFieldToken(fieldData.field)

				// Emit drop event with payload context
				this.$emit('field-drop', {
					field: fieldData.field,
					token: token,
					context: 'payload',
					position: insertionPosition,
					targetElement: targetElement
				})

				// Insert token into the target element
				this.insertTokenIntoElement(targetElement, token, insertionPosition)
			} catch (error) {
				showSnackbar({ text: error, color: 'red' })
			} finally {
				this.draggedField = null
			}
		},

		hasValidFieldData (event) {
			try {
				const data = event.dataTransfer.getData('application/json')
				return data && JSON.parse(data)
			} catch (error) {
				return false
			}
		},

		extractFieldData (event) {
			try {
				const data = event.dataTransfer.getData('application/json')
				return JSON.parse(data)
			} catch (error) {
				return null
			}
		},

		findTargetElement (event) {
			// Look for various input elements including code editors
			const selectors = [
				'input',
				'textarea',
				'.CodeMirror textarea',
				'.ace_text-input',
				'[contenteditable="true"]',
				'.json-editor textarea',
				'.json-editor input'
			]

			const inputs = this.$el.querySelectorAll(selectors.join(','))

			// If there's only one input, use it
			if (inputs.length === 1) {
				return inputs[0]
			}

			// If no standard inputs found, look for the JsonEditor component itself
			if (inputs.length === 0) {
				const jsonEditor = this.$el.querySelector('.json-editor, [class*="json-editor"], [class*="CodeMirror"]')
				if (jsonEditor) {
					// Return the JsonEditor container, we'll handle it specially
					return jsonEditor
				}
			}

			// Otherwise, find the closest input to the drop position
			let closestInput = null
			let closestDistance = Infinity

			inputs.forEach(input => {
				const rect = input.getBoundingClientRect()
				const distance = Math.sqrt(
					Math.pow(event.clientX - (rect.left + rect.width / 2), 2) +
          Math.pow(event.clientY - (rect.top + rect.height / 2), 2)
				)

				if (distance < closestDistance) {
					closestDistance = distance
					closestInput = input
				}
			})

			return closestInput
		},

		calculateInsertionPosition (element, event) {
			if (!element) return 0

			// For text inputs, try to calculate cursor position based on click position
			if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
				const rect = element.getBoundingClientRect()
				const relativeX = event.clientX - rect.left
				// const relativeY = event.clientY - rect.top  // Not used in current implementation

				// Simple approximation - in a real implementation, you'd use more sophisticated positioning
				const charWidth = 8 // Average character width
				const approximatePosition = Math.max(0, Math.floor(relativeX / charWidth))

				return Math.min(approximatePosition, element.value.length)
			}

			return 0
		},

		insertTokenIntoElement (element, token, position) {
			if (!element) return

			// Check if this is a standard input/textarea element
			if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
				if (element.readOnly || element.disabled) return

				const currentValue = element.value || ''
				const newValue = currentValue.slice(0, position) + token + currentValue.slice(position)

				// Update the element value
				element.value = newValue

				// Trigger input event to notify Vue of the change
				element.dispatchEvent(new Event('input', { bubbles: true }))

				// Set cursor position after the inserted token
				const newCursorPosition = position + token.length
				element.setSelectionRange(newCursorPosition, newCursorPosition)

				// Focus the element
				element.focus()
			} else {
				// For JsonEditor or other complex components, emit the token insertion
				// The parent component will handle updating the JsonEditor value
				this.$emit('token-insert', {
					token: token,
					position: position,
					element: element
				})
			}
		},

		generateFieldToken (field) {
			// Generate clean field token from field name
			const fieldPart = field.fieldName.split('.').pop() || field.fieldName
			const cleanFieldName = fieldPart.replace(/_/g, '').toUpperCase()
			return `[PLACEHOLDER_${cleanFieldName}]`
		}
	}
}
</script>

<style lang="scss" scoped>
.drop-zone {
  position: relative;

  &--active {
    .drop-zone__content {
      position: relative;
      z-index: 1;
    }

    .drop-zone__content :deep(input),
    .drop-zone__content :deep(textarea) {
      border-color: var(--global-color-blue-darken-2) !important;
      box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2) !important;
    }
  }

  &--disabled {
    pointer-events: none;
    opacity: 0.6;
  }
}

.drop-zone__content {
  position: relative;
  z-index: 2;
}

.drop-zone__indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(33, 150, 243, 0.1);
  border: 2px dashed var(--global-color-blue-darken-2);
  border-radius: var(--radius-sm, 4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: none;

  &-content {
    display: flex;
    align-items: center;
    gap: 8px;
    background: white;
    padding: 8px 16px;
    border-radius: var(--radius-full, 9999px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--global-color-blue-darken-2);
  }

  &-text {
    font-size: 14px;
    color: var(--global-color-blue-darken-2);
    font-weight: 500;
  }
}

// Enhanced styling for child input elements
.drop-zone :deep(input),
.drop-zone :deep(textarea) {
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--global-color-blue-darken-2);
  }

  &:focus {
    border-color: var(--global-color-blue-darken-2);
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
  }
}

// Special styling for text fields within drop zones
.drop-zone :deep(.v-text-field) {
  position: relative;

  .v-input__control {
    position: relative;
  }

  .v-input__slot {
    transition: all 0.2s ease;
  }

  &:hover .v-input__slot {
    border-color: var(--global-color-blue-darken-2);
  }
}

// Ensure drop zone works with Vuetify components
.drop-zone :deep(.v-input--is-focused) {
  .v-input__slot {
    border-color: var(--global-color-blue-darken-2) !important;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2) !important;
  }
}
</style>
